/*
 * Copyright (c) Nexign, JSC, 1992-2024.
 */
/**
 *
 * @author Julia Ignatieva
 * @version 1.29.0
 * @since 1.29.0
 */

import { createElement } from 'react';

import isEmpty from 'lodash/isEmpty';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { removeEmptyValuesFromObject } from '@/main/utils/removeEmptyValues';
import { HttpStatus } from '@modules/app/appConstants';
import { makeSelectBreadCrumbsData } from '@modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@modules/breadCrumbs/utils/breadCrumbsGoBack';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardAdditionalReports,
    makeSelectWizardTranslation,
} from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { ECalendarPeriod } from '@modules/internationalization/types/common/calendar';
import { IWizardTranslation } from '@modules/internationalization/types/wizards/wizard';
import ModalErrorCommon from '@modules/modal/components/ModalError';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { EWizardReportParam } from '@modules/wizard/components/WizardReport/WizardReportConstants';
import {
    wizardReportStorageRestoreSaga,
    wizardReportValidateSaga,
} from '@modules/wizard/components/WizardReport/wizardReportSagas';
import {
    makeSelectWizardReportOrderParams,
    makeSelectWizardReportParam,
} from '@modules/wizard/components/WizardReport/wizardReportSelectors';
import {
    IWizardReportOrderParams,
    IWizardReportParam,
} from '@modules/wizard/components/WizardReport/wizardReportTypes';
import {
    wizardNextAction,
    wizardOpenModalSummaryAction,
    wizardStorageSaveStepAction,
} from '@modules/wizard/wizardActions';
import {
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_ACTION,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_REPORT_SET_PARAM,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
} from '@modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '@modules/wizard/wizardSagas';
import {
    makeSelectWizardBasketSnapshotId, makeSelectWizardBasketType,
    makeSelectWizardStepName,
} from '@modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction, TWizardStepNames } from '@modules/wizard/wizardTypes';
import { makeSelectAuthoritiesIsServiceUser } from '@pages/PageAuth/selectors/authSelectors';
import {
    WizardAdditionalReportsAutoReportResultModal,
} from '@pages/WizardAdditionalReports/components/WizardAdditionalReportsAutoReportResultModal';
import {
    makeSelectWizardAdditionalReportsParam,
} from '@pages/WizardAdditionalReports/wizardAdditionalReportsSelectors';

import {
    IEditableId,
    TWizardAdditionalReportsSetParamsPayload,
} from './types/wizardAdditionalReportsActionTypes';
import {
    wizardAdditionalReportsSetParamsAction,
    wizardAdditionalReportsValidateFinishAction,
} from './wizardAdditionalReportsActions';
import {
    EWizardAdditionalReports,
    EWizardAdditionalReportsSteps,
    WIZARD_ADDITIONAL_REPORTS_HANDLE_NEXT,
    WIZARD_ADDITIONAL_REPORTS_SET_PARAMS,
    WIZARD_ADDITIONAL_REPORTS_VALIDATE_START,
} from './wizardAdditionalReportsConsant';

/*
* -------------------------------------------
*               INIT
* -------------------------------------------
*/
export function* wizardAdditionalReportsStepInitSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName: string = yield select(makeSelectWizardStepName());
    const { step } = action.payload;

    yield call(wizardAdditionalReportsStorageRestoreSaga);

    switch (stepName) {
        case EWizardAdditionalReportsSteps.ChooseStep:
            yield put(wizardAdditionalReportsSetParamsAction({
                reportsTypes: step.reportsTypes || [],
            }));
            break;
        case EWizardAdditionalReportsSteps.PrepareStep:
            const ids = yield select(makeSelectWizardAdditionalReportsParam('ids'));
            yield put(wizardAdditionalReportsSetParamsAction({
                isAutoReportEnabled: step.isAutoReportEnabled,
                formatsAvailable: step.formatsAvailable || [],
                formatChoose: step.formatsAvailable?.[0] ?? '',
                maxAutoReportsCount: step.maxAutoReportsCount,
                ids: (Array.isArray(ids) && ids.length > 0)
                    ? ids : step.ids?.map((item: IEditableId) => ( { ...item, checked: true } )),
            }));
            break;
        case EWizardAdditionalReportsSteps.ConfirmStep:
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }
}

/*
* -------------------------------------------
*               STORAGE
* -------------------------------------------
*/
export function* wizardAdditionalReportsStorageRestoreSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());

    const localStorageParams = localStorageState[basketSnapshotId] ?? {};

    const keys: Partial<Array<keyof TWizardAdditionalReportsSetParamsPayload>> = [
        'autoReportDescription',
        'autoReportHour',
        'autoReportMinutes',
        'autoReportMonth',
        'autoReportType',
        'autoReportWeek',
        'chosenReport',
        'formatChoose',
        'isCreateOrderNow',
        'ids',
    ];

    const params: TWizardAdditionalReportsSetParamsPayload =
        keys.reduce((acc: Record<string, string>, cur?: string) => {
            if (!!cur && localStorageParams[cur]) {
                acc[cur] = localStorageParams[cur];
            }
            return acc;
        }, {});

    yield put(wizardAdditionalReportsSetParamsAction(params));
}

export function* wizardAdditionalReportsStorageSaveSaga(): SagaIterator {
    yield put(wizardStorageSaveStepAction({
        params: {
            autoReportDescription: yield select(makeSelectWizardAdditionalReportsParam('autoReportDescription')),
            autoReportHour: yield select(makeSelectWizardAdditionalReportsParam('autoReportHour')),
            autoReportMinutes: yield select(makeSelectWizardAdditionalReportsParam('autoReportMinutes')),
            autoReportMonth: yield select(makeSelectWizardAdditionalReportsParam('autoReportMonth')),
            autoReportType: yield select(makeSelectWizardAdditionalReportsParam('autoReportType')),
            autoReportWeek: yield select(makeSelectWizardAdditionalReportsParam('autoReportWeek')),
            chosenReport: yield select(makeSelectWizardAdditionalReportsParam('chosenReport')),
            formatChoose: yield select(makeSelectWizardAdditionalReportsParam('formatChoose')),
            isCreateOrderNow: yield select(makeSelectWizardAdditionalReportsParam('isCreateOrderNow')),
            ids: yield select(makeSelectWizardAdditionalReportsParam('ids')),
        },
    }));
}

/*
* -------------------------------------------
*               VALIDATE
* -------------------------------------------
*/
export function* wizardAdditionalReportsValidateSaga(): SagaIterator {
    let isStepValid = true;
    let validateError = '';
    const stepName = yield select(makeSelectWizardStepName());
    const isCreateOrderNow = yield select(makeSelectWizardAdditionalReportsParam('isCreateOrderNow'));

    switch (stepName) {
        case EWizardAdditionalReportsSteps.ChooseStep:
            break;
        case EWizardAdditionalReportsSteps.PrepareStep:
            const autoReportMonth = yield select(makeSelectWizardAdditionalReportsParam('autoReportMonth'));
            const autoReportType = yield select(makeSelectWizardAdditionalReportsParam('autoReportType'));
            if (!isCreateOrderNow && (autoReportType === ECalendarPeriod.M)
                && (isNaN(Number(autoReportMonth)) || autoReportMonth < 1 || autoReportMonth > 31)) {
                isStepValid = false;
            }
            break;
        case EWizardAdditionalReportsSteps.ConfirmStep:
            const isServiceUser: boolean = yield select(makeSelectAuthoritiesIsServiceUser());
            if (!isCreateOrderNow && isServiceUser) {
                const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());
                validateError = commonTranslation.serviceUser.tooltip;
                isStepValid = false;
                break;
            }

            isStepValid = yield call(wizardReportValidateSaga);
            break;
    }

    yield put(wizardAdditionalReportsValidateFinishAction({
        isStepValid,
        validateError,
    }));
}

/*
* -------------------------------------------
*               STEP
* -------------------------------------------
*/

export function* wizardAdditionalReportsHandleNextSaga(): SagaIterator {
    const stepName: TWizardStepNames = yield select(makeSelectWizardStepName());
    const chosenReport = yield select(makeSelectWizardAdditionalReportsParam('chosenReport'));

    switch (stepName) {
        case EWizardAdditionalReportsSteps.ChooseStep:
            yield put(wizardNextAction({
                reportType: chosenReport,
            }));
            break;
        case EWizardAdditionalReportsSteps.PrepareStep:
            yield put(wizardNextAction({
                data: {},
            }));
            break;
        case EWizardAdditionalReportsSteps.ConfirmStep:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());

            const reportDestEmail: IWizardReportParam =
                yield select(makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST));

            const autoReportDescription = yield select(makeSelectWizardAdditionalReportsParam('autoReportDescription'));
            const autoReportHour = yield select(makeSelectWizardAdditionalReportsParam('autoReportHour'));
            const autoReportMinutes = yield select(makeSelectWizardAdditionalReportsParam('autoReportMinutes'));
            const autoReportMonth = yield select(makeSelectWizardAdditionalReportsParam('autoReportMonth'));
            const autoReportType = yield select(makeSelectWizardAdditionalReportsParam('autoReportType'));
            const autoReportWeek = yield select(makeSelectWizardAdditionalReportsParam('autoReportWeek'));
            const basketType = yield select(makeSelectWizardBasketType());
            const formatChoose = yield select(makeSelectWizardAdditionalReportsParam('formatChoose'));
            const isCreateOrderNow = yield select(makeSelectWizardAdditionalReportsParam('isCreateOrderNow'));
            const ids = yield select(makeSelectWizardAdditionalReportsParam('ids'));
            const newSubscribersEndDate = yield select(makeSelectWizardAdditionalReportsParam('newSubscribersEndDate'));
            const newSubscribersStartDate = yield select(makeSelectWizardAdditionalReportsParam('newSubscribersStartDate'));

            const data = removeEmptyValuesFromObject({
                autoReportDescription: isCreateOrderNow ? undefined : autoReportDescription,
                autoReportHour: isCreateOrderNow ? undefined : autoReportHour,
                autoReportMinutes: isCreateOrderNow ? undefined : autoReportMinutes,
                autoReportMonth: (!isCreateOrderNow && autoReportType === ECalendarPeriod.M)
                    ? autoReportMonth : undefined,
                autoReportType: isCreateOrderNow ? undefined : autoReportType,
                autoReportWeek: (!isCreateOrderNow && autoReportType === ECalendarPeriod.W)
                    ? +autoReportWeek + 1 : undefined,
                basketType: basketType,
                format: formatChoose,
                ids: ids,
                isCreateOrderNow: isCreateOrderNow,
                newSubscribersEndDate: chosenReport === EWizardAdditionalReports.REPORT_SUBSCRIBERS_NEW
                    ? newSubscribersEndDate : undefined,
                newSubscribersStartDate: chosenReport === EWizardAdditionalReports.REPORT_SUBSCRIBERS_NEW
                    ? newSubscribersStartDate : undefined,
                reportType: chosenReport,
            });

            const notifyParams = removeEmptyValuesFromObject({
                ...reportParams.notify,
                destEmail: reportDestEmail.isEnabled ? reportDestEmail.value : undefined,
            });

            const params = !isEmpty(notifyParams) ? { notify: notifyParams } : {};

            yield put(wizardNextAction({
                data,
                ...params,
            }));
            break;
    }
}

export function* wizardAdditionalReportsNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    if (stepName === EWizardAdditionalReportsSteps.ConfirmStep) {
        const isCreateOrderNow = yield select(makeSelectWizardAdditionalReportsParam('isCreateOrderNow'));
        const chosenReport = yield select(makeSelectWizardAdditionalReportsParam('chosenReport'));
        const translation = yield select(makeSelectTranslationWizardAdditionalReports());
        const translationCommon = yield select(makeSelectTranslationCommon());
        const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';
        if (isCreateOrderNow) {
            yield put(wizardOpenModalSummaryAction({
                orderId: orderId,
                orderType: translation.reports[chosenReport],
            }));
        } else {
            const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
            const goBack = () => breadCrumbGoBack(breadCrumbsData);
            yield put(
                modalOpen({
                    onCrossClickCallback: goBack,
                    render: (props: IModalRenderProps) =>
                        createElement(WizardAdditionalReportsAutoReportResultModal, {
                            dataName: 'wizard-auto-report-summary',
                            onCloseButtonClick: () => {
                                props.onCloseModal();
                                goBack();
                            },
                            autoReportId: orderId,
                            autoReportType: translation.reports[chosenReport],
                            onCloseModal: props.onCloseModal,
                            translation: translation,
                            translationCommon: translationCommon,
                        }),
                    modalParams: {
                        closeable: false,
                    },
                }),
            );
        }
    }
}

/*
* -------------------------------------------
*              Failure Handler
* -------------------------------------------
*/
export function* wizardAdditionalReportsFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const wizardTranslation: IWizardTranslation = yield select(makeSelectWizardTranslation());
        title = translationCommon.error.codes[errorCode]
            || wizardTranslation.wizardModalError.default;
    }

    yield put(modalOpen({
        render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
            onCloseModal: props.onCloseModal,
            title,
            translation: translationCommon.modal,
        }),
    }));
}

export function* wizardAdditionalReportsSagas(): SagaIterator {
    yield all([
        takeEvery(WIZARD_META_SUCCESS, wizardAdditionalReportsStepInitSaga),
        takeEvery([
            WIZARD_ADDITIONAL_REPORTS_SET_PARAMS,
            WIZARD_ADDITIONAL_REPORTS_VALIDATE_START,
            WIZARD_REPORT_SET_PARAM,
        ], wizardAdditionalReportsValidateSaga),
        takeEvery(WIZARD_ADDITIONAL_REPORTS_HANDLE_NEXT, wizardAdditionalReportsHandleNextSaga),
        takeEvery([
            WIZARD_ADDITIONAL_REPORTS_SET_PARAMS,
            WIZARD_NEXT_ACTION,
        ], wizardAdditionalReportsStorageSaveSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, wizardAdditionalReportsNextSuccessSaga),
        takeEvery(WIZARD_BACK_SUCCESS, wizardAdditionalReportsStorageRestoreSaga),
        takeEvery([
            WIZARD_BACK_FAILED,
            WIZARD_NEXT_FAILED,
            WIZARD_RESTORE_FAILED,
            WIZARD_START_FAILED,
        ], wizardAdditionalReportsFailedSaga),
    ]);
}
